import { fetchAllPages } from '@frontend/api-utils';
import { EntityType } from '@frontend/common';
import { UserGroupClient } from '@frontend/group/api';
import { GroupMember, GroupMemberQueryParams, IGroup } from '@frontend/group/types';
import { useModals } from '@frontend/modal';
import { useAccountRepository } from '@frontend/repository';
import { ToastUtil } from '@frontend/toast-utils';
import { TransactionUserClient, TransactionWorkflowClient } from '@frontend/transaction/api';
import { ExtendedNivalizTransaction, ExtendedPudoTransaction, TransactionTriggerName, TransactionUserType } from '@frontend/transaction/types';
import { IUser } from '@frontend/user/types';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

export type DelegationPartyType = 'receiver' | 'sender';

export interface DelegateTransactionModalProps {
    transaction: ExtendedPudoTransaction | ExtendedNivalizTransaction;
    onSubmitCallback?: () => void;
}

async function fetchAllUserIdsInGroup(groupId: string, visitedGroupIds: Set<string> = new Set()): Promise<string[]> {
    if (visitedGroupIds.has(groupId)) return [];
    visitedGroupIds.add(groupId);

    const members = await fetchAllPages<GroupMember, GroupMemberQueryParams>((params) => UserGroupClient.fetchUserGroupMembers(params), { group_id: groupId });

    const userIds: string[] = [];
    for (const member of members) {
        if (member.entity_type === EntityType.USER) {
            userIds.push(member.entity_id);
            continue;
        }

        if (member.entity_type === EntityType.USER_GROUP) {
            userIds.push(...(await fetchAllUserIdsInGroup(member.entity_id, visitedGroupIds)));
        }
    }

    return userIds;
}

export const useDelegateTransactionModal = (props: DelegateTransactionModalProps) => {
    const intl = useIntl();
    const { closeTopModal } = useModals();
    const { currentAccount } = useAccountRepository();

    const [step, setStep] = useState<'select' | 'confirm'>('select');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [delegationType, setDelegationType] = useState<DelegationPartyType>('receiver');
    const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
    const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
    const [userGroupMembers, setUserGroupMembers] = useState<Map<string, string[]>>(new Map());
    const previousSelectionRef = useRef<{ userIds: string[]; groupIds: string[] }>({ userIds: [], groupIds: [] });
    const groupMemberCache = useRef<Map<string, string[]>>(new Map());

    const existingPartyIds = useMemo(() => {
        const parties = delegationType === 'receiver' ? props.transaction.receivers : props.transaction.senders;
        return parties?.map((party) => party.entity_id).filter(Boolean) ?? [];
    }, [delegationType, props.transaction.receivers, props.transaction.senders]);

    const clearSelection = useCallback(() => {
        setSelectedUserIds([]);
        setSelectedGroupIds([]);
        setUserGroupMembers(new Map());
        previousSelectionRef.current = { userIds: [], groupIds: [] };
    }, []);

    const handleDelegationTypeChange = useCallback(
        (nextType: DelegationPartyType) => {
            if (nextType === delegationType) return;
            setDelegationType(nextType);
            clearSelection();
            setStep('select');
        },
        [clearSelection, delegationType]
    );

    const handlePartyChange = useCallback(
        async (selectedUsers: IUser[], selectedGroups: IGroup[]) => {
            const newGroupIds = selectedGroups.map((group) => group.id);
            const newUserIds = selectedUsers.map((user) => user.id);

            const addedGroupIds = newGroupIds.filter((id) => !previousSelectionRef.current.groupIds.includes(id));
            const removedGroupIds = previousSelectionRef.current.groupIds.filter((id) => !newGroupIds.includes(id));

            const updatedMap = new Map(userGroupMembers);
            removedGroupIds.forEach((groupId) => {
                updatedMap.delete(groupId);
            });

            const fetchedGroups = await Promise.all(
                addedGroupIds.map(async (groupId) => {
                    if (groupMemberCache.current.has(groupId)) {
                        return { groupId, userIds: groupMemberCache.current.get(groupId)! };
                    }

                    const userIds = await fetchAllUserIdsInGroup(groupId);
                    groupMemberCache.current.set(groupId, userIds);
                    return { groupId, userIds };
                })
            );

            fetchedGroups.forEach(({ groupId, userIds }) => {
                updatedMap.set(groupId, userIds);
            });

            setUserGroupMembers(updatedMap);
            setSelectedGroupIds(newGroupIds);
            setSelectedUserIds(newUserIds);
            previousSelectionRef.current = { userIds: newUserIds, groupIds: newGroupIds };
        },
        [userGroupMembers]
    );

    const getAllSelectedUserIds = useCallback((): string[] => {
        const groupUserIds = Array.from(userGroupMembers.values()).flat();
        return [...new Set([...selectedUserIds, ...groupUserIds])];
    }, [selectedUserIds, userGroupMembers]);

    const newPartyUserIds = useMemo(() => {
        return getAllSelectedUserIds().filter((userId) => !existingPartyIds.includes(userId));
    }, [getAllSelectedUserIds, existingPartyIds]);

    const handlePrimaryAction = async () => {
        if (step === 'select') {
            if (newPartyUserIds.length === 0) {
                ToastUtil.warning(
                    intl.formatMessage(
                        delegationType === 'receiver'
                            ? {
                                  id: 'platform.transaction.delegate-transaction-modal.no-new-receivers-warning',
                                  description: 'Warning shown when no new receivers are selected in the delegate transaction modal',
                                  defaultMessage: 'Select at least one user or group that is not already a receiver.'
                              }
                            : {
                                  id: 'platform.transaction.delegate-transaction-modal.no-new-senders-warning',
                                  description: 'Warning shown when no new senders are selected in the delegate transaction modal',
                                  defaultMessage: 'Select at least one user or group that is not already a sender.'
                              }
                    )
                );
                return;
            }

            setStep('confirm');
            return;
        }

        setIsSubmitting(true);
        try {
            const userType = delegationType === 'receiver' ? TransactionUserType.RECEIVER : TransactionUserType.SENDER;
            const trigger =
                delegationType === 'receiver'
                    ? TransactionTriggerName.TRANSACTION_DELEGATED_TO_RECEIVER
                    : TransactionTriggerName.TRANSACTION_DELEGATED_TO_SENDER;
            const triggerData = delegationType === 'receiver' ? { receiver_ids: newPartyUserIds } : { sender_ids: newPartyUserIds };

            await Promise.all(
                newPartyUserIds.map((userId) =>
                    TransactionUserClient.postTransactionUser(props.transaction.account_id, props.transaction.id, {
                        user_id: userId,
                        type: userType
                    })
                )
            );

            await TransactionWorkflowClient.triggerTransactionState(props.transaction.account_id, props.transaction.id, {
                trigger,
                data: triggerData
            });

            ToastUtil.success(
                intl.formatMessage(
                    delegationType === 'receiver'
                        ? {
                              id: 'platform.transaction.delegate-transaction-modal.success',
                              description: 'Toast shown when a transaction is delegated successfully',
                              defaultMessage: '{count, plural, one {# user was added as a receiver.} other {# users were added as receivers.}}'
                          }
                        : {
                              id: 'platform.transaction.delegate-transaction-modal.success-senders',
                              description: 'Toast shown when a transaction is delegated successfully to senders',
                              defaultMessage: '{count, plural, one {# user was added as a sender.} other {# users were added as senders.}}'
                          },
                    { count: newPartyUserIds.length }
                )
            );

            props.onSubmitCallback?.();
            closeTopModal();
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        if (step === 'confirm') {
            setStep('select');
            return;
        }

        closeTopModal();
    };

    return {
        step,
        isSubmitting,
        currentAccount,
        delegationType,
        selectedUserIds,
        selectedGroupIds,
        newPartyUserIds,
        handleDelegationTypeChange,
        handlePartyChange,
        handlePrimaryAction,
        handleCancel
    };
};
